import { encodeSshPublicKey } from '@emma/core/device';

const SUPPORTED_KEY_TYPES = ['ssh-ed25519', 'ssh-rsa'] as const;
const BASE64_REGEX = /^[a-zA-Z0-9+/=]+$/;

export const MAX_EXPIRY_HOURS = 24;

export type SshKeyValidationResult =
  | { error: string }
  | { error: null; key: string; expiresAt: string | undefined };

function extractBase64Key(key: string): string | null {
  const parts = key.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const base64Part = parts[1];
  if (!BASE64_REGEX.test(base64Part)) return null;
  return base64Part;
}

function validateExpiresInHours(hours: number): string | null {
  if (!Number.isFinite(hours) || !Number.isInteger(hours))
    return 'Expiration must be a whole number';
  if (hours <= 0 || hours > MAX_EXPIRY_HOURS) return 'Expiration must be between 1 and 24 hours';
  return null;
}

export function validateSshPublicKey(key: string, expiresInHours: number): SshKeyValidationResult {
  const trimmed = key.trim();
  if (!trimmed) return { error: 'Public key is required' };

  const keyType = trimmed.split(/\s+/)[0];
  const hasValidType = SUPPORTED_KEY_TYPES.some((type) => type === keyType);
  if (!hasValidType) return { error: 'Only ssh-ed25519 and ssh-rsa key types are supported' };

  if (!extractBase64Key(trimmed)) return { error: 'Invalid SSH public key format' };

  const hoursError = validateExpiresInHours(expiresInHours);
  if (hoursError) return { error: hoursError };

  const expiresAt =
    expiresInHours === MAX_EXPIRY_HOURS
      ? undefined
      : new Date(Date.now() + expiresInHours * 60 * 60 * 1000).toISOString();

  return { error: null, key: encodeSshPublicKey(trimmed), expiresAt };
}
